/**
 * Per-wallet transaction history.
 *
 * Primary source: nt3d subgraph v0.1.0 (keyBuys + payouts filtered by the wallet).
 * Fallback: diamond event logs over a bounded recent block range, fetched in small
 * chunks with backoff on HTTP 429 so the public Base Sepolia RPC isn't hammered.
 */
import { ethers } from 'ethers';
import { getReadProvider, getGameAddress } from './contract';

const SUBGRAPH_URL = import.meta.env.VITE_SUBGRAPH_URL ||
  'https://api.studio.thegraph.com/query/1724403/notomorrow-3-d-sepolia/v0.1.0';

export type HistoryType = 'purchase' | 'withdraw' | 'referral' | 'win' | 'team_bonus' | 'pioneer';

export interface HistoryTx {
  id: string;
  type: HistoryType;
  amount: bigint;
  keys?: bigint;
  /** contract faction id (1-8), 0 = none */
  team?: number;
  /** ms since epoch */
  timestamp: number;
  txHash: string;
  round?: number;
}

export interface HistoryResult {
  txs: HistoryTx[];
  source: 'subgraph' | 'rpc';
  note?: string;
}

const PAYOUT_TYPE: Record<string, HistoryType> = {
  WINNER: 'win',
  PIONEER: 'pioneer',
  FACTION_CLAIM: 'team_bonus',
  JACKPOT_REFERRER: 'referral',
  DIVIDEND_WITHDRAWAL: 'withdraw',
};

const big = (v: unknown) => { try { return BigInt(v as any); } catch { return 0n; } };

async function fromSubgraph(wallet: string, limit: number): Promise<HistoryTx[] | null> {
  try {
    const res = await fetch(SUBGRAPH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `query History($w: String!, $n: Int!) {
          keyBuys(first: $n, orderBy: timestamp, orderDirection: desc, where: { buyer: $w }) {
            id keysBought pawSpent teamId timestamp txHash round { roundId }
          }
          payouts(first: $n, orderBy: timestamp, orderDirection: desc, where: { recipient: $w }) {
            id kind amount faction timestamp txHash round { roundId }
          }
        }`,
        variables: { w: wallet.toLowerCase(), n: limit },
      }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (json.errors || !json.data) return null;
    const buys: HistoryTx[] = (json.data.keyBuys || []).map((k: any) => ({
      id: k.id,
      type: 'purchase',
      amount: big(k.pawSpent),
      keys: big(k.keysBought),
      team: Number(k.teamId ?? 0),
      timestamp: Number(k.timestamp) * 1000,
      txHash: k.txHash,
      round: k.round ? Number(k.round.roundId) : undefined,
    }));
    const pays: HistoryTx[] = (json.data.payouts || [])
      .filter((p: any) => big(p.amount) > 0n)
      .map((p: any) => ({
        id: p.id,
        type: PAYOUT_TYPE[p.kind] ?? 'win',
        amount: big(p.amount),
        team: p.faction != null ? Number(p.faction) : undefined,
        timestamp: Number(p.timestamp) * 1000,
        txHash: p.txHash,
        round: p.round ? Number(p.round.roundId) : undefined,
      }));
    return [...buys, ...pays].sort((a, b) => b.timestamp - a.timestamp);
  } catch {
    return null;
  }
}

const EVENTS = new ethers.Interface([
  'event KeyPurchased(address indexed buyer, uint256 pawSpent, uint256 keysBought, uint256 dividendsDistributed, uint256 newTotalKeys, uint256 newPot, uint256 newDeadline, uint256 currentPrice, uint8 teamId, bool isGhostKey, uint8 comboMultiplier, string message)',
  'event DividendsWithdrawn(address indexed holder, uint256 amount)',
  'event RoundEnded(uint256 indexed roundId, address indexed winner, uint256 potSize, uint256 winnerPayout, uint256 dividendDistributed, uint256 rolloverAmount, uint256 communityShare, bool wasBloodMoon)',
  'event PioneerPaid(uint256 indexed roundId, address indexed winner, uint256 amount, uint256 pioneerKeys)',
  'event FactionJackpotClaimed(uint256 indexed roundId, address indexed player, uint8 faction, uint256 amount)',
]);

/** Blocks scanned by the RPC fallback (~11h on Base Sepolia at 2s blocks). */
export const RPC_FALLBACK_BLOCKS = Number(import.meta.env.VITE_HISTORY_RPC_BLOCKS || 20_000);
const CHUNK = 2_000;
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
const is429 = (e: any) => /429|rate limit|too many/i.test(String(e?.status ?? e?.info?.responseStatus ?? e?.message ?? e?.shortMessage ?? ''));

async function getLogsWithBackoff(provider: ethers.JsonRpcProvider, filter: ethers.Filter, tries = 4): Promise<ethers.Log[]> {
  let delay = 800;
  for (let i = 0; ; i++) {
    try {
      return await provider.getLogs(filter);
    } catch (e) {
      if (i >= tries - 1 || !is429(e)) throw e;
      await sleep(delay);
      delay *= 2;
    }
  }
}

async function fromRpc(wallet: string): Promise<HistoryResult> {
  const provider = getReadProvider();
  const address = getGameAddress();
  const head = await provider.getBlock('latest');
  if (!head) return { txs: [], source: 'rpc', note: 'RPC unavailable.' };
  const topicWallet = ethers.zeroPadValue(wallet.toLowerCase(), 32);
  const t = (name: string) => EVENTS.getEvent(name)!.topicHash;
  // buyer/holder are topic1; RoundEnded winner, PioneerPaid winner, FactionJackpotClaimed player are topic2.
  const topic1Sets = [[t('KeyPurchased'), t('DividendsWithdrawn')]];
  const topic2Sets = [[t('RoundEnded'), t('PioneerPaid'), t('FactionJackpotClaimed')]];
  const from = Math.max(0, head.number - RPC_FALLBACK_BLOCKS);
  const logs: ethers.Log[] = [];
  let partial = false;
  for (let end = head.number; end > from; end -= CHUNK) {
    const start = Math.max(from, end - CHUNK + 1);
    try {
      const [a, b] = await Promise.all([
        getLogsWithBackoff(provider, { address, fromBlock: start, toBlock: end, topics: [topic1Sets[0], topicWallet] }),
        getLogsWithBackoff(provider, { address, fromBlock: start, toBlock: end, topics: [topic2Sets[0], null, topicWallet] }),
      ]);
      logs.push(...a, ...b);
    } catch (e) {
      partial = true; // stop scanning further back after a hard failure / persistent 429
      console.warn('[history] RPC log scan stopped:', (e as any)?.shortMessage || e);
      break;
    }
    await sleep(150);
  }
  // Base Sepolia produces a block every 2s: estimate timestamps without one call per block.
  const tsOf = (bn: number) => (head.timestamp - (head.number - bn) * 2) * 1000;
  const txs: HistoryTx[] = [];
  for (const log of logs) {
    let ev: ethers.LogDescription | null = null;
    try { ev = EVENTS.parseLog(log); } catch { /* not ours */ }
    if (!ev) continue;
    const base = { id: `${log.transactionHash}-${log.index}`, timestamp: tsOf(log.blockNumber), txHash: log.transactionHash };
    switch (ev.name) {
      case 'KeyPurchased':
        txs.push({ ...base, type: 'purchase', amount: ev.args.pawSpent, keys: ev.args.keysBought, team: Number(ev.args.teamId) });
        break;
      case 'DividendsWithdrawn':
        txs.push({ ...base, type: 'withdraw', amount: ev.args.amount });
        break;
      case 'RoundEnded':
        if (ev.args.winnerPayout > 0n) txs.push({ ...base, type: 'win', amount: ev.args.winnerPayout, round: Number(ev.args.roundId) });
        break;
      case 'PioneerPaid':
        txs.push({ ...base, type: 'pioneer', amount: ev.args.amount, round: Number(ev.args.roundId) });
        break;
      case 'FactionJackpotClaimed':
        txs.push({ ...base, type: 'team_bonus', amount: ev.args.amount, team: Number(ev.args.faction), round: Number(ev.args.roundId) });
        break;
    }
  }
  txs.sort((a, b) => b.timestamp - a.timestamp);
  const hours = Math.round((RPC_FALLBACK_BLOCKS * 2) / 3600);
  return {
    txs,
    source: 'rpc',
    note: `Indexer unavailable, showing the last ~${hours}h from the chain${partial ? ' (partial: RPC rate-limited)' : ''}.`,
  };
}

export async function getHistory(wallet: string, limit = 100): Promise<HistoryResult> {
  const sub = await fromSubgraph(wallet, limit);
  if (sub) return { txs: sub, source: 'subgraph' };
  return fromRpc(wallet);
}

/** KeyPurchased from a buy receipt (the buyer's own log), or null. */
export function parseKeyPurchased(receipt: { logs?: readonly any[] } | null | undefined, buyer?: string | null): { keys: bigint; spent: bigint; faction: number } | null {
  for (const log of receipt?.logs ?? []) {
    try {
      const ev = EVENTS.parseLog(log);
      if (ev?.name !== 'KeyPurchased') continue;
      if (buyer && String(ev.args.buyer).toLowerCase() !== buyer.toLowerCase()) continue;
      return { keys: ev.args.keysBought, spent: ev.args.pawSpent, faction: Number(ev.args.teamId) };
    } catch { /* other contract's log */ }
  }
  return null;
}
